import { useQuery } from '@apollo/client'
import { useState, useEffect } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'

import {
  Button,
  Card,
  CardBox,
  CardActions,
  CardContent,
  Icons,
  Stack,
  Text,
  useAlert,
  NotificationDialog,
} from '../../components'
import { queries } from '../../graphql'
import { BankAccountSetup } from '../zumrails-iframe'

export function ManageZumBankAccounts(props) {
  const { manageBankAccountsRef } = props
  const navigate = useNavigate()
  const { donorFundId } = useOutletContext() ?? {}
  const [open, setOpen] = useState(false)
  const [cannotDeleteOpen, setCannotDeleteOpen] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const [{ Alert, alertProps }, { setAlert }] = useAlert()
  const { data: zumRailsbankAccountsData, refetch } = useQuery(queries.paymentMethods.linkedZumRailsBanks, {
    variables: { fundId: donorFundId },
  })
  const myFund = useQuery(queries.funds.myFund)
  const [bankAccounts, setBankAccounts] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (zumRailsbankAccountsData) {
      setBankAccounts(zumRailsbankAccountsData.linkedZumRailsBanks)
    }
  }, [zumRailsbankAccountsData])

  const hasBankAccounts = bankAccounts && bankAccounts.length

  const handleDelete = async () => {
    setIsLoading(true)
    try {
      const response = await fetch(`${process.env.REACT_APP_API_HOST}/zumrails/delete-bank-account/${deletingId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${JSON.parse(localStorage.getItem('givewise_auth_state'))?.state?.rawToken}`,
        },
      })

      if (!response.ok) {
        let errorMessage = 'Failed to delete bank account from ZumRails'
        try {
          const errorData = await response.json()
          errorMessage = errorData.error || errorMessage
        } catch (parseError) {
          errorMessage = response.statusText || errorMessage
        }
        throw new Error(errorMessage)
      }

      setBankAccounts((prevAccounts) => prevAccounts.filter((ba) => ba.zumUserId !== deletingId))
      setAlert({ message: 'Bank account removed successfully.', severity: 'success' })
    } catch (error) {
      setAlert({ message: `An error occurred: ${error.message}`, severity: 'error' })
    } finally {
      setIsLoading(false)
      setOpen(false)
    }
  }

  const handleConfirm = (id) => {
    setOpen(true)
    setDeletingId(id)
  }

  const handleDialogClose = () => {
    setOpen(false)
    setCannotDeleteOpen(false)
    setDeletingId(null)
  }

  return (
    <Stack ref={manageBankAccountsRef}>
      <CardBox>
        <Stack direction="row">
          <Text.H4>Bank Accounts</Text.H4>
        </Stack>
        <br />
        <Stack direction="row" flexWrap="wrap" justifyContent="flex-start">
          {hasBankAccounts ? (
            bankAccounts.map((ba) => {
              const srcObj = ba

              return (
                <Card
                  key={ba.Id}
                  variant="outlined"
                  style={{
                    minWidth: 300,
                    maxWidth: 300,
                    margin: 10,
                    backgroundColor: 'lightblue',
                    borderColor: 'lightblue',
                  }}
                >
                  <CardContent>
                    <Text.H5>{`${srcObj.Institution}`}</Text.H5>
                    <br />
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Text.Body>{`••••••••${srcObj.AccountNumber.slice(-4)}`}</Text.Body>
                    </Stack>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      color="error"
                      variant="text"
                      onClick={ba.canDelete ? () => handleConfirm(ba.zumUserId) : () => setCannotDeleteOpen(true)}
                    >
                      <Icons.DeleteForever fontSize="small" />
                      Remove
                    </Button>
                  </CardActions>
                </Card>
              )
            })
          ) : (
            <BankAccountSetup refetchAccount={refetch} activeFundId={donorFundId} />
          )}
        </Stack>
        <br />
        <NotificationDialog
          open={cannotDeleteOpen}
          onClose={handleDialogClose}
          type="alert"
          message={
            <>
              This bank account cannot be removed because it is linked to a scheduled or recurring contribution.
              <br />
              <br />
              To remove this payment method, please first cancel any scheduled or recurring contributions.
            </>
          }
          actions={[
            { label: 'Close', onClick: handleDialogClose },
            {
              label: 'Go to my activity',
              onClick: () => navigate(`/funds/${myFund.data?.me?.fund.id}/activity/giving-wallet`),
              autoFocus: true,
            },
          ]}
        />
        <NotificationDialog
          open={open}
          onClose={handleDialogClose}
          type="alert"
          title="Remove Bank Account"
          message="Are you sure you want to remove this bank account from your profile?"
          actions={[
            { label: 'Cancel', onClick: handleDialogClose },
            { label: 'Yes, remove it', onClick: handleDelete, autoFocus: true },
          ]}
        />
        <Alert {...alertProps} />
      </CardBox>
    </Stack>
  )
}
